import React from 'react';
import { CompanyProjectSubmissionResponse } from '../../api/admin';
import { useGetCompanyProjectSubmissions } from '../../hooks/use-hydra/admin';
import styled from 'styled-components';
import day from 'dayjs';
import utc from 'dayjs/plugin/utc';
day.extend(utc);
import { createColumnHelper } from '@tanstack/react-table';
import { Table } from '../Table/CompactTable';
import NameCellContents from './Cells/Name';
import { SmallPortalTooltip } from '../Tooltip';
import { useHistory } from 'react-router-dom';
import { SmallBadge } from '../elements/Badge';
import { PSmall } from '../elements/Text';
import { useTheme } from 'styled-components';
import { DefaultTheme } from 'styled-components';
import {
  TooltipContent,
  TooltipDetail,
  TooltipTitle,
} from './Progress/Tooltip';
import {
  CollapsibleSectionHeader,
  CollapsibleSectionLabel,
} from './CollapsibleSection';
import CollapsibleSection from './CollapsibleSection';
import { ROUTES } from '../../routing';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 32px;
`;

export default function CompanyProjects({ companyId }: { companyId: number }) {
  const { data } = useGetCompanyProjectSubmissions(companyId);

  const groupedData = useGroupDataByCohort(data?.submissions ?? []);

  return (
    <Wrapper>
      {Object.entries(groupedData)
        .sort((a, b) => {
          const aStart = a[1][0]?.cohort?.startDate;
          const bStart = b[1][0]?.cohort?.startDate;
          return day(aStart).isAfter(bStart) ? -1 : 1;
        })
        .map(([cohortId, submissions], index) => (
          <Cohort
            key={cohortId}
            submissions={submissions}
            companyId={companyId}
            isFirstCohort={index === 0}
          />
        ))}
    </Wrapper>
  );
}

const useGroupDataByCohort = (
  submissions: CompanyProjectSubmissionResponse['submissions']
) => {
  const groupedData = React.useMemo(() => {
    // Group submissions by cohort
    const groupedByCohort = submissions.reduce((acc, submission) => {
      const cohortId = submission.cohort.id;
      if (!acc[cohortId]) {
        acc[cohortId] = [];
      }
      acc[cohortId].push(submission);
      return acc;
    }, {} as Record<number, CompanyProjectSubmissionResponse['submissions']>);

    return groupedByCohort;
  }, [submissions]);

  return groupedData;
};

const columnHelper =
  createColumnHelper<CompanyProjectSubmissionResponse['submissions'][number]>();

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
`;

const BadgeText = styled(PSmall)`
  text-align: center;

  margin-top: 0;
  padding-bottom: 0;
`;

const StatusIcon = styled.i<{ color: string }>`
  font-size: 15px;
  margin: 0;
  padding: 0;
  color: ${(props) => props.color};
`;

export function getMilestoneStyles(
  milestone: CompanyProjectSubmissionResponse['submissions'][number]['PROJECT_CHECKIN_1'],
  cohort: CompanyProjectSubmissionResponse['submissions'][number]['cohort'],
  theme: DefaultTheme
) {
  if (milestone?.isCompleted) {
    return {
      status: 'Submitted',
      faIcon: 'fa-solid fa-check',
      badgeColor: theme.colors.buttonPrimary,
      textColor: theme.colors.bgPage,
    };
  }
  const didSendNotification = (milestone?.notifications ?? [])?.some(
    (n) => !!n.sent_at
  );
  if (didSendNotification) {
    return {
      status: 'Notified',
      faIcon: 'fa-solid fa-bell',
      badgeColor: theme.colors.buttonSecondary,
      textColor: theme.colors.bgPage,
    };
  }
  if (milestone?.isLocked) {
    return {
      status: 'Locked',
      faIcon: 'fa-solid fa-lock',
      badgeColor: theme.colors.textDisabled,
      textColor: theme.colors.bgPage,
    };
  }

  if (day.utc().isAfter(day.utc(cohort.endDate))) {
    return {
      status: 'Not Submitted',
      faIcon: 'fa-solid fa-xmark',
      badgeColor: theme.colors.bgDefault,
      textColor: theme.colors.textActive,
    };
  }

  return {
    status: 'Unlocked',
    faIcon: 'fa-solid fa-unlock',
    badgeColor: theme.colors.textActive,
    textColor: theme.colors.bgPage,
  };
}

const SubmissionTooltipContent = ({
  title,
  submission,
  submsssionStyle,
}: {
  title: string;
  submission:
    | CompanyProjectSubmissionResponse['submissions'][number]['PROJECT_CHECKIN_1']
    | CompanyProjectSubmissionResponse['submissions'][number]['PROJECT_CHECKIN_2']
    | CompanyProjectSubmissionResponse['submissions'][number]['PROJECT_UPLOAD'];
  submsssionStyle: ReturnType<typeof getMilestoneStyles>;
}) => {
  const didSendNotification = (submission?.notifications ?? [])?.some(
    (n) => !!n.sent_at
  );
  const didSubmit = submission?.isCompleted;
  return (
    <TooltipContent>
      <TooltipTitle>{title}</TooltipTitle>
      <TooltipDetail>Status - {submsssionStyle.status}</TooltipDetail>
      <>
        <TooltipTitle style={{ marginTop: '16px' }}>Unlock</TooltipTitle>
        <TooltipDetail>
          {submission?.isLocked ? 'Unlocks at' : 'Unlocked at'}{' '}
          {day(submission?.unlockedAt).format('MM/DD/YYYY')}
        </TooltipDetail>
      </>

      {didSendNotification && (
        <>
          <TooltipTitle style={{ marginTop: '16px' }}>
            Notifications
          </TooltipTitle>
          <TooltipDetail>
            Notifications sent at{' '}
            {submission?.notifications
              ?.filter((x) => !!x.sent_at)
              .map((x) => day.utc(x.sent_at).format('MM/DD/YYYY'))
              .join(', ')}
          </TooltipDetail>
        </>
      )}
      {didSubmit && (
        <>
          <TooltipTitle style={{ marginTop: '16px' }}>Submission</TooltipTitle>
          <TooltipDetail>
            Submitted at {day(submission?.submittedAt).format('MM/DD/YYYY')}
          </TooltipDetail>
        </>
      )}
    </TooltipContent>
  );
};

// Remove MilestonesCell and instead create individual cell renderers for each milestone column

const CheckIn1Cell = ({
  submission,
}: {
  submission: CompanyProjectSubmissionResponse['submissions'][number];
}) => {
  const theme = useTheme();
  const milestone = getMilestoneStyles(
    submission.PROJECT_CHECKIN_1,
    submission.cohort,
    theme
  );
  const tooltipId = `checkin-1-tooltip-${submission.submissionId}`;
  return (
    <>
      <SmallBadge
        colors={{
          textColor: milestone.textColor,
          backgroundColor: milestone.badgeColor,
        }}
      >
        <Row data-tooltip-id={tooltipId}>
          <StatusIcon
            color={milestone.textColor}
            className={milestone.faIcon}
          />
          <BadgeText>{milestone.status}</BadgeText>
        </Row>
      </SmallBadge>
      <SmallPortalTooltip anchorSelect={`[data-tooltip-id="${tooltipId}"]`}>
        <SubmissionTooltipContent
          title="Check-In #1"
          submission={submission.PROJECT_CHECKIN_1}
          submsssionStyle={milestone}
        />
      </SmallPortalTooltip>
    </>
  );
};

const CheckIn2Cell = ({
  submission,
}: {
  submission: CompanyProjectSubmissionResponse['submissions'][number];
}) => {
  const theme = useTheme();
  const milestone = getMilestoneStyles(
    submission.PROJECT_CHECKIN_2,
    submission.cohort,
    theme
  );
  const tooltipId = `checkin-2-tooltip-${submission.submissionId}`;
  return (
    <>
      <SmallBadge
        colors={{
          textColor: milestone.textColor,
          backgroundColor: milestone.badgeColor,
        }}
      >
        <Row data-tooltip-id={tooltipId}>
          <StatusIcon
            color={milestone.textColor}
            className={milestone.faIcon}
          />
          <BadgeText>{milestone.status}</BadgeText>
        </Row>
      </SmallBadge>
      <SmallPortalTooltip anchorSelect={`[data-tooltip-id="${tooltipId}"]`}>
        <SubmissionTooltipContent
          title="Check-In #2"
          submission={submission.PROJECT_CHECKIN_2}
          submsssionStyle={milestone}
        />
      </SmallPortalTooltip>
    </>
  );
};

const ProjectUploadCell = ({
  submission,
}: {
  submission: CompanyProjectSubmissionResponse['submissions'][number];
}) => {
  const theme = useTheme();
  const milestone = getMilestoneStyles(
    submission.PROJECT_UPLOAD,
    submission.cohort,
    theme
  );
  const tooltipId = `project-upload-tooltip-${submission.submissionId}`;
  return (
    <>
      <SmallBadge
        colors={{
          textColor: milestone.textColor,
          backgroundColor: milestone.badgeColor,
        }}
      >
        <Row data-tooltip-id={tooltipId}>
          <StatusIcon
            color={milestone.textColor}
            className={milestone.faIcon}
          />
          <BadgeText>{milestone.status}</BadgeText>
        </Row>
      </SmallBadge>
      <SmallPortalTooltip anchorSelect={`[data-tooltip-id="${tooltipId}"]`}>
        <SubmissionTooltipContent
          title="Project Upload"
          submission={submission.PROJECT_UPLOAD}
          submsssionStyle={milestone}
        />
      </SmallPortalTooltip>
    </>
  );
};
const CommentText = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CommentCell = ({
  submission,
}: {
  submission: CompanyProjectSubmissionResponse['submissions'][number];
}) => {
  const tooltipId = `comment-tooltip-${submission.submissionId}`;

  if (!submission.comment) {
    return <div>-</div>;
  }

  return (
    <div data-tooltip-id={tooltipId}>
      <CommentText>{submission.comment?.comment}</CommentText>
      <SmallPortalTooltip anchorSelect={`[data-tooltip-id="${tooltipId}"]`}>
        <TooltipContent>
          <TooltipTitle>Comment</TooltipTitle>
          <TooltipDetail style={{ fontStyle: 'italic' }}>
            Comment left by {submission.comment?.commented_by?.name} on{' '}
            {day.utc(submission.comment?.commented_at).format('MM/DD/YYYY')}
          </TooltipDetail>
          <TooltipDetail style={{ marginTop: '16px' }}>
            {submission.comment?.comment}
          </TooltipDetail>
        </TooltipContent>
      </SmallPortalTooltip>
    </div>
  );
};

const columns = [
  columnHelper.accessor('learner', {
    header: () => 'Learner',
    cell: (info) => (
      <NameCellContents
        name={info.getValue().name}
        email={info.getValue().email}
        tooltipId={`learner-${info.row.id}`}
        isBold
      />
    ),
    meta: {
      styles: {
        maxWidth: '200px',
        minWidth: '200px',
      },
    },
  }),
  columnHelper.display({
    id: 'checkin1',
    header: () => 'Check-In #1',
    cell: (info) => <CheckIn1Cell submission={info.row.original} />,
    meta: {
      styles: {
        minWidth: '200px',
        textAlign: 'left',
      },
    },
  }),
  columnHelper.display({
    id: 'checkin2',
    header: () => 'Check-In #2',
    cell: (info) => <CheckIn2Cell submission={info.row.original} />,
    meta: {
      styles: {
        minWidth: '200px',
        textAlign: 'left',
      },
    },
  }),
  columnHelper.display({
    id: 'projectupload',
    header: () => 'Project Upload',
    cell: (info) => <ProjectUploadCell submission={info.row.original} />,
    meta: {
      styles: {
        minWidth: '200px',
        textAlign: 'left',
      },
    },
  }),
  columnHelper.display({
    id: 'comment',
    header: () => 'Comment',
    cell: (info) => <CommentCell submission={info.row.original} />,
    meta: {
      styles: {
        minWidth: '300px',
        maxWidth: '300px',
        textAlign: 'left',
      },
    },
  }),
];

const TableWrapper = styled.div`
  .cohort-progress-table {
    th,
    td {
      &:last-child {
        border-right: none !important;
      }
      &:first-child {
        border-left: none !important;
      }
    }

    tbody > tr:last-child > td {
      border-bottom: none;
    }

    th {
      border-radius: 0 !important;
    }
  }
`;

const Cohort = ({
  submissions,
  companyId,
  isFirstCohort,
}: {
  submissions: CompanyProjectSubmissionResponse['submissions'];
  isFirstCohort: boolean;
  companyId: number;
}) => {
  const [expanded, setExpanded] = React.useState(isFirstCohort);
  const cohort = submissions[0].cohort;

  const history = useHistory();

  return (
    <CollapsibleSection
      headerLeft={
        <>
          <CollapsibleSectionHeader>
            {day.utc(cohort.startDate).format('DD MMMM YYYY')} to{' '}
            {day.utc(cohort.endDate).format('DD MMMM YYYY')}
          </CollapsibleSectionHeader>
        </>
      }
      headerRight={
        <CollapsibleSectionLabel>
          {submissions.length}{' '}
          {submissions.length === 1 ? 'Learner' : 'Learners'}
        </CollapsibleSectionLabel>
      }
      expanded={expanded}
      onToggle={() => setExpanded((prev) => !prev)}
    >
      <TableWrapper>
        <Table
          className="cohort-progress-table"
          data={submissions}
          columns={columns}
          getRowId={(row) => row.submissionId.toString()}
          getRowProps={(row) => ({
            onClick: () =>
              history.push(
                ROUTES.ADMIN.COMPANY_PROJECT_SUBMISSION.getPath({
                  companyId: companyId.toString(),
                  submissionId: row.original.submissionId.toString(),
                })
              ),
          })}
        />
      </TableWrapper>
    </CollapsibleSection>
  );
};
